<template>
  <v-dialog
    v-model="dialog"
    width="500px"
    style="z-index: 9999;"
  >
    <v-card>
      <v-toolbar dense elevation="0">
        <v-toolbar-title>{{ $t("dialog.update_ride.title") }}</v-toolbar-title>
        <div class="flex-grow-1"></div>
        <v-btn @click="store" elevation="0" color="primary">
          {{ $t("dialog.update_ride.store") }}
        </v-btn>
        <v-btn icon @click="dialog=false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-divider class="mx-2" />

      <v-container>
        <validation-observer ref="form_validation">
          <form @submit.prevent="submit">
            <v-row dense class="px-2">
              <v-col cols="1">
                <v-checkbox v-model="rideTypeEnabled" />
              </v-col>
              <v-col cols="11">
                <validation-provider
                  v-slot="{ errors }"
                  name="dialog.update_ride.ride_type"
                  rules="required"
                >
                  <v-select
                    v-model="rideType"
                    :items="getRideTypes"
                    item-text="code"
                    :label="$t('dialog.update_ride.ride_type')"
                    :error-messages="errors"
                    clearable
                    :disabled="!rideTypeEnabled"
                  >
                    <template v-slot:selection="data">
                      {{ $t("enum.ride_type." + data.item) }}
                    </template>
                    <template v-slot:item="data">
                      {{ $t("enum.ride_type." + data.item) }}
                    </template>
                  </v-select>
                </validation-provider>
              </v-col>
            </v-row>

            <v-row dense class="px-2">
              <v-col cols="1">
                <v-checkbox v-model="kmStopEnabled" />
              </v-col>
              <v-col cols="11">
                <validation-provider
                  v-slot="{ errors }"
                  name="dialog.update_ride.stop_km"
                  rules="decimal:3"
                >
                  <v-text-field
                    v-model="kmStop"
                    class="blue-hint"
                    :error-messages="errors"
                    :label="$t('dialog.update_ride.stop_km')"
                    :disabled="!kmStopEnabled"
                    clearable
                    :hint="(kmFixed) ? $t('dialog.update_ride.stop_fix') : null"
                    persistent-hint
                  ></v-text-field>
                </validation-provider>
              </v-col>
            </v-row>

            <v-row dense class="px-2">
              <v-col cols="1">
                <v-checkbox v-model="ehStopEnabled" />
              </v-col>
              <v-col cols="11">
                <validation-provider
                  v-slot="{ errors }"
                  name="dialog.update_ride.stop_eh"
                  rules="decimal:2"
                >
                  <v-text-field
                    v-model="ehStop"
                    class="blue-hint"
                    :error-messages="errors"
                    :label="$t('dialog.update_ride.stop_eh')"
                    :disabled="!ehStopEnabled"
                    clearable
                    :hint="(ehFixed) ? $t('dialog.update_ride.stop_fix') : null"
                    persistent-hint
                  ></v-text-field>
                </validation-provider>
              </v-col>
            </v-row>

            <v-row dense class="px-2">
              <v-col cols="1">
                <v-checkbox v-model="driverEnabled" />
              </v-col>
              <v-col cols="11">
                <validation-provider
                  v-slot="{ errors }"
                  name="dialog.update_ride.ride_type"
                  rules=""
                >
                  <PersonField v-model="driver"
                               :error-messages="errors"
                               :disabled="!driverEnabled"
                               :label="$t('dialog.update_ride.driver')"
                  />
                </validation-provider>
              </v-col>
            </v-row>

            <v-row dense class="px-2">
              <v-col cols="1">
                <v-checkbox v-model="noteEnabled" />
              </v-col>
              <v-col cols="11">
                <validation-provider
                  v-slot="{ errors }"
                  name="dialog.update_ride.note"
                  rules="max:30"
                >
                  <v-text-field
                    v-model="note"
                    :error-messages="errors"
                    :label="$t('dialog.update_ride.note')"
                    :disabled="!noteEnabled"
                    clearable
                  ></v-text-field>
                </validation-provider>
              </v-col>
            </v-row>
          </form>
        </validation-observer>
      </v-container>
    </v-card>

    <v-snackbar
      v-model="showError"
      bottom
      light
      timeout="-1"
      color="red lighten-4"
    >
      {{ errorMessage }}

      <template v-slot:action="{ attrs }">
        <v-btn
          color="indigo"
          text
          v-bind="attrs"
          @click="showError = false"
        >
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </v-dialog>
</template>

<script>

import PersonField from "@/components/fields/PersonField.vue";
import utils from "@/utils";

export default {
  name: "RideUpdateDialog",
  components: { PersonField },

  data() {
    return {
      dialog: false,
      ride: null,
      rideTypeEnabled: false,
      rideType: null,
      kmStopEnabled: false,
      kmStop: null,
      kmFixed: false,
      ehStopEnabled: false,
      ehStop: null,
      ehFixed: false,
      driverEnabled: false,
      driver: null,
      noteEnabled: false,
      note: null,

      showError: false,
      errorMessage: null,
    };
  },

  methods: {
    open(ride) {
      this.ride = ride;
      this.rideType = ride.rideType;
      this.kmStop = utils.numberToString(ride.kmStop / 1000, 3);
      this.kmFixed = ride.fixedKm;
      this.ehStop = utils.numberToString(ride.mhStop / 3600, 2);
      this.ehFixed = ride.fixedMh;
      this.driver = ride.driver;
      this.note = ride.note;

      this.rideTypeEnabled = false;
      this.kmStopEnabled = false;
      this.ehStopEnabled = false;
      this.driverEnabled = false;
      this.noteEnabled = false;

      this.dialog = true;
      if (this.$refs.form_validation !== undefined)
        this.$refs.form_validation.reset();
    },

    async store() {
      if (await this.$refs.form_validation.validate()) {
        let update = {};

        if (this.rideTypeEnabled)
          update["rideType"] = this.rideType;
        if (this.kmStopEnabled)
          update["kmStop"] = (this.kmStop !== null) ? utils.toNumber(this.kmStop) * 1000 : null;
        if (this.ehStopEnabled)
          update["ehStop"] = (this.ehStop !== null) ? utils.toNumber(this.ehStop) * 3600 : null;
        if (this.driverEnabled)
          update["driverId"] = (this.driver !== null) ? this.driver.personId : null;
        if (this.noteEnabled)
          update["note"] = this.note;

        let response = await this.$store.dispatch("data/updateRide", {
          vehicleId: this.ride.vehicleId,
          rideStart: this.ride.fromTimestamp,
          body: update
        });

        if (response.error) {
          this.showError = true;
          this.errorMessage = response.message;
        } else {
          this.$store.dispatch("history/refresh");
          this.$store.dispatch("logbook/refresh");

          this.dialog = false;
        }

      }
    }
  },

  watch: {
    dialog(newValue) {
      if (newValue === false)
        this.$store.dispatch("dialogs/hideDialog", "rideUpdate");
    }
  },

  computed: {
    getRideTypes() {
      return this.$store.state.data.listRideTypes;
    }
  },

  mounted() {
    this.$store.dispatch("data/fetchCodes", "rideTypes");
  }
};
</script>
